import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Star, Heart, Shield, Truck, RefreshCcw, Check, Minus, Plus, ShoppingBag, Zap } from "lucide-react";
import { useProduct, useCatalog } from "../lib/catalog";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import ProductCard from "../components/ProductCard";
import { toast } from "sonner";

// Convert YouTube/Vimeo watch links to embed URLs
function toEmbedUrl(url) {
  if (!url) return "";
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

export default function ProductDetail() {
  const { slug } = useParams();
  const { product, loading, error } = useProduct(slug);
  const { products: allProducts, categories } = useCatalog();
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();
  const { toggle, has } = useWishlist();
  const navigate = useNavigate();

  if (loading) {
    return <main className="max-w-[1400px] mx-auto px-4 py-20 text-center text-neutral-500 animate-pulse">Loading product...</main>;
  }
  if (error || !product) {
    return (
      <main className="max-w-[1400px] mx-auto px-4 py-20 text-center">
        <h1 className="font-display text-3xl font-black">Product not found</h1>
        <Link to="/shop" className="mt-4 inline-block text-[#FF5A1F] font-semibold">← Back to shop</Link>
      </main>
    );
  }

  const discount = Math.round(((product.mrp - product.sale) / product.mrp) * 100);
  const inWish = has(product.id);
  const cat = categories.find((c) => c.slug === product.category);
  const related = allProducts.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4);

  const handleAdd = () => {
    addItem(product, qty);
    toast.success("Added to cart", { description: `${qty} × ${product.title}` });
  };
  const handleBuy = () => {
    addItem(product, qty);
    navigate("/checkout");
  };

  return (
    <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 md:py-12" data-testid="product-detail">
      <div className="text-xs text-neutral-500 mb-4 md:mb-6 truncate">
        <Link to="/" className="hover:text-neutral-900">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/shop" className="hover:text-neutral-900">Shop</Link>
        {cat && <>
          <span className="mx-2">/</span>
          <Link to={`/shop/${cat.slug}`} className="hover:text-neutral-900">{cat.name}</Link>
        </>}
      </div>

      <div className="grid md:grid-cols-2 gap-6 md:gap-10 lg:gap-16">
        <div>
          <div className="aspect-square bg-neutral-100 rounded-2xl md:rounded-3xl overflow-hidden flex items-center justify-center">
            <img src={product.images[activeImg]} alt={product.title} className="max-w-full max-h-full object-contain p-6 md:p-10" data-testid="main-image" />
          </div>
          <div className="grid grid-cols-5 md:grid-cols-6 gap-2 mt-3 md:mt-4">
            {product.images.map((img, i) => (
              <button key={i} onClick={() => setActiveImg(i)}
                className={`aspect-square bg-neutral-100 rounded-lg md:rounded-xl overflow-hidden border-2 ${activeImg === i ? "border-[#FF5A1F]" : "border-transparent"}`}
                data-testid={`thumb-${i}`}>
                <img src={img} alt="" className="w-full h-full object-contain p-1.5" />
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">{product.brand}</div>
          <h1 className="font-display text-2xl md:text-4xl font-black leading-tight mt-1.5 md:mt-2 text-neutral-900" data-testid="product-title">{product.title}</h1>
          <p className="text-neutral-500 mt-1 text-sm md:text-base">{product.subtitle}</p>

          <div className="flex items-center gap-3 mt-3 md:mt-4">
            <div className="flex items-center gap-1 text-sm">
              <Star className="w-4 h-4 fill-[#FF5A1F] text-[#FF5A1F]" />
              <span className="font-bold">{product.rating}</span>
              <span className="text-neutral-500">· {product.reviews} reviews</span>
            </div>
          </div>

          <div className="flex items-baseline gap-3 mt-4 md:mt-5 flex-wrap">
            <span className="font-display text-2xl md:text-4xl font-black">₹{product.sale}</span>
            <span className="text-base md:text-lg text-neutral-400 line-through">₹{product.mrp}</span>
            {discount > 0 && <span className="bg-[#FF5A1F]/10 text-[#FF5A1F] text-xs md:text-sm font-bold px-2.5 py-1 rounded-full">{discount}% OFF</span>}
          </div>
          <div className="text-[11px] md:text-xs text-neutral-500 mt-1">Inclusive of all taxes</div>

          <div className="mt-5 md:mt-6 grid grid-cols-2 gap-3 text-xs md:text-sm">
            {product.color && <div><span className="text-neutral-500">Colour: </span><span className="font-semibold">{product.color}</span></div>}
            {product.material && <div><span className="text-neutral-500">Material: </span><span className="font-semibold">{product.material}</span></div>}
          </div>

          <div className="mt-5 md:mt-7 flex items-center gap-3">
            <div className="flex items-center border border-neutral-300 rounded-full h-11 md:h-12">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 md:w-11 h-11 md:h-11 flex items-center justify-center" data-testid="qty-decrease"><Minus className="w-4 h-4" /></button>
              <span className="w-8 text-center font-semibold" data-testid="qty-display">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="w-10 md:w-11 h-11 md:h-11 flex items-center justify-center" data-testid="qty-increase"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={() => { toggle(product.id); toast.success(inWish ? "Removed from wishlist" : "Added to wishlist"); }}
              className={`w-11 h-11 md:w-12 md:h-12 rounded-full border flex items-center justify-center transition ${inWish ? "bg-[#FF5A1F] text-white border-[#FF5A1F]" : "border-neutral-300 hover:border-neutral-900"}`}
              data-testid="detail-wishlist-btn">
              <Heart className="w-5 h-5" fill={inWish ? "currentColor" : "none"} />
            </button>
          </div>

          <div className="mt-3 md:mt-4 grid grid-cols-2 gap-2 md:gap-3">
            <button onClick={handleAdd} className="h-12 md:h-14 rounded-full border-2 border-neutral-900 font-bold text-xs md:text-sm flex items-center justify-center gap-1.5 md:gap-2 hover:bg-neutral-900 hover:text-white transition px-2" data-testid="add-to-cart-btn">
              <ShoppingBag className="w-4 h-4" /> Add to Cart
            </button>
            <button onClick={handleBuy} className="h-12 md:h-14 rounded-full bg-[#FF5A1F] hover:bg-[#e64a0f] text-white font-bold text-xs md:text-sm flex items-center justify-center gap-1.5 md:gap-2 transition px-2" data-testid="buy-now-btn">
              <Zap className="w-4 h-4" /> Buy Now
            </button>
          </div>

          <div className="mt-5 md:mt-6 grid grid-cols-3 gap-2 md:gap-3 text-[11px] md:text-xs">
            <div className="bg-neutral-50 rounded-xl p-2.5 md:p-3 flex flex-col items-start gap-1"><Shield className="w-4 h-4 text-[#FF5A1F]" /><span className="font-semibold leading-tight">RFID Secure</span></div>
            <div className="bg-neutral-50 rounded-xl p-2.5 md:p-3 flex flex-col items-start gap-1"><Truck className="w-4 h-4 text-[#FF5A1F]" /><span className="font-semibold leading-tight">Free Delivery</span></div>
            <div className="bg-neutral-50 rounded-xl p-2.5 md:p-3 flex flex-col items-start gap-1"><RefreshCcw className="w-4 h-4 text-[#FF5A1F]" /><span className="font-semibold leading-tight">10-Day Return</span></div>
          </div>

          {product.aboutItem?.length > 0 && (
            <div className="mt-6 md:mt-8">
              <h3 className="font-display text-lg md:text-xl font-black">About this item</h3>
              <ul className="mt-3 space-y-2.5">
                {product.aboutItem.map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs md:text-sm text-neutral-700">
                    <Check className="w-4 h-4 text-[#FF5A1F] mt-0.5 flex-shrink-0" /><span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {product.description && (
            <div className="mt-5 md:mt-6">
              <h3 className="font-display text-lg md:text-xl font-black">Description</h3>
              <p className="mt-3 text-xs md:text-sm text-neutral-700 leading-relaxed">{product.description}</p>
            </div>
          )}

          {product.videoUrl && (
            <div className="mt-6 md:mt-8" data-testid="product-video">
              <h3 className="font-display text-lg md:text-xl font-black">Product Video</h3>
              <div className="mt-3 aspect-video rounded-2xl overflow-hidden bg-black">
                <iframe
                  src={toEmbedUrl(product.videoUrl)}
                  title={`${product.title} video`}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}

          {product.keyFeatures?.length > 0 && (
            <div className="mt-5 md:mt-6">
              <h3 className="font-display text-lg md:text-xl font-black">Key Features</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {product.keyFeatures.map((k, i) => (
                  <span key={i} className="text-[11px] md:text-xs font-medium bg-neutral-100 text-neutral-800 px-3 py-1.5 rounded-full">{k}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-12 md:mt-20">
          <div className="flex items-end justify-between mb-6 md:mb-8 gap-3">
            <h2 className="font-display text-2xl md:text-3xl font-black leading-tight">You may also like</h2>
            <Link to={`/shop/${product.category}`} className="text-xs md:text-sm font-semibold hover:text-[#FF5A1F] whitespace-nowrap">View all →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-7">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </main>
  );
}
